import type { HttpTypes } from "@medusajs/types";
import { useCallback } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { useUpdateCartsAddresses } from "@/queries/cart/mutations";
import { toCheckoutAddress } from "@/pages/checkout/resolve-addresses";
import type {
  CheckoutAddressFormData,
  InlineAddressData
} from "@/pages/checkout/validations";

/**
 * Handle real-time shipping address changes during checkout.
 * Updates all carts immediately when address changes so that:
 * - Shipping options are recalculated based on new location
 * - Shipping prices can be updated based on postal code rules
 *
 * Writes the same address the eventual submit will write. Both go through
 * `resolveCheckoutAddresses`/{@link toCheckoutAddress}, including the rule that
 * the form's contact phone beats the address's own on shipping, so this write
 * and the submit cannot land different values for one address.
 */
export function useShippingAddressChange({
  cartIds,
  form,
  updateAddressesMutation
}: {
  cartIds: string[];
  form: UseFormReturn<CheckoutAddressFormData>;
  updateAddressesMutation: ReturnType<typeof useUpdateCartsAddresses>;
}) {
  return useCallback(
    (address: HttpTypes.StoreCustomerAddress | InlineAddressData) => {
      const shippingAddressData = toCheckoutAddress(
        address,
        form.getValues("phone")
      );

      // Update carts with new shipping address
      // If "same as shipping" is checked, also update billing address
      updateAddressesMutation.mutate({
        cartIds,
        shippingAddress: shippingAddressData,
        billingAddress: form.getValues("sameAsShipping")
          ? { ...shippingAddressData }
          : undefined
      });
    },
    [cartIds, form, updateAddressesMutation]
  );
}
